import { useState, useCallback } from 'react';
import { useUnnestCampaign } from './campaignNesting/useUnnestCampaign';
import { useNestCampaign } from './campaignNesting/useNestCampaign';
import { useFixOrphanedCampaigns } from './campaignNesting/useFixOrphanedCampaigns';
import { useReorderNestedCampaign } from './campaignNesting/useReorderNestedCampaign';

/**
 * Hook for managing campaign nesting operations
 * 
 * UNIFIED DATA MODEL:
 * - All items (campaigns AND templates) are in a single sprints array
 * - Templates are distinguished by isTemplate: true flag
 * - Nesting works the same way for both - no special handling needed
 * 
 * Handles:
 * - Nesting a campaign inside another campaign OR template (creating a linked task node)
 * - Unnesting a campaign (promoting back to top-level)
 * - Fixing orphaned campaigns (promoting them back to top-level)
 * - Reordering nested campaigns within their parent
 * 
 * Returns isNesting state to show loading indicator during operations
 */
export const useCampaignNesting = (setSprints) => {
  // Loading state for nesting operations
  const [isNesting, setIsNesting] = useState(false);
  
  // All hooks work on the unified sprints array
  const { unnestCampaign } = useUnnestCampaign(setSprints);
  const { nestCampaign } = useNestCampaign(setSprints);
  const { fixOrphanedCampaigns } = useFixOrphanedCampaigns(setSprints);
  const { reorderNestedCampaign } = useReorderNestedCampaign(setSprints);

  /**
   * Nest a campaign inside another campaign, or unnest it to root level.
   * 
   * When targetSprintId is provided:
   *   - Creates a new task node in the target campaign that links to the dragged campaign
   * 
   * When targetSprintId is null:
   *   - Promotes the campaign to root level (unnesting)
   *   - Removes the task link from the old parent
   * 
   * IMPORTANT: Always fetches fresh data from API to avoid overwriting unsaved changes!
   * 
   * @param {string} draggedSprintId - ID of the campaign being nested/unnested
   * @param {string|null} targetSprintId - ID of the campaign to nest into, or null to unnest
   * @param {string} missionName - Name for the new task node (ignored when unnesting)
   * @param {Object} options - Optional configuration
   * @param {Object} options.position - {x, y} position for the new node (used when dropping into flow view)
   */
  const handleNestCampaign = useCallback(async (draggedSprintId, targetSprintId, missionName, options = {}) => {
    setIsNesting(true);
    
    try {
      if (targetSprintId === null) {
        // Unnesting: promote to root level
        await unnestCampaign(draggedSprintId);
      } else {
        // Nesting: nest into target campaign
        await nestCampaign(draggedSprintId, targetSprintId, missionName, options);
      }
    } catch (error) {
      console.error('[NEST] Failed to nest campaign:', error);
    } finally {
      setIsNesting(false);
    }
  }, [unnestCampaign, nestCampaign]);

  /**
   * Reorder a nested campaign within its parent's childOrder.
   * This is called when dragging a nested campaign above/below a sibling.
   * 
   * @param {string} draggedId - ID of the campaign being dragged
   * @param {string} targetId - ID of the sibling campaign to position relative to
   * @param {string} position - 'before' or 'after' the target
   * @param {string} parentId - ID of the parent campaign containing both
   * @param {string[]} currentVisibleOrder - The current order of siblings as displayed in the UI
   */
  const handleReorderNestedCampaign = useCallback(async (draggedId, targetId, position, parentId, currentVisibleOrder) => {
    await reorderNestedCampaign(draggedId, targetId, position, parentId, currentVisibleOrder);
  }, [reorderNestedCampaign]);

  return {
    handleNestCampaign,
    handleReorderNestedCampaign,
    fixOrphanedCampaigns,
    isNesting,
  };
};

export default useCampaignNesting;
